import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface PageViewLog {
  pageName: string;
  pagePath: string;
  timestamp: Date;
  duration?: number; // tempo em segundos
}

export interface FeatureUsageLog {
  featureName: string;
  featureCategory: string;
  action: string; // 'click', 'view', 'use', etc.
  timestamp: Date;
  details?: Record<string, any>;
}

export interface ErrorLog {
  message: string;
  stack?: string;
  source?: string; // arquivo/linha
  timestamp: Date;
  page?: string;
  userAgent?: string;
  severity: 'error' | 'warning' | 'info';
}

class UsageTrackingService {
  private currentPageStartTime: number = Date.now();
  private currentPage: string = '';
  private errorLogs: ErrorLog[] = [];
  private pageViews: PageViewLog[] = [];
  private featureUsage: FeatureUsageLog[] = [];
  private initialized: boolean = false;

  /** Ruído esperado (rede/cache/deploy) — não deve inflar o painel de erros do usuário. */
  private isNoiseError(message: string): boolean {
    const m = (message || '').toLowerCase();
    return (
      m.includes('detectar localização por ip') ||
      m.includes('localização por ip') ||
      m.includes('ipapi.co') ||
      (m.includes('failed to fetch') && (m.includes('localiza') || m.includes('ip'))) ||
      m.includes('chunkloaderror') ||
      m.includes('loading chunk') ||
      m.includes('loading css chunk') ||
      (m.includes('unexpected token') && m.includes('<')) ||
      m.includes('chunkloaderror: loading chunk') ||
      m.includes('não é possível agendar para data/horário passado') || // validação de negócio, não crash
      m.includes('amount deve ser maior que zero') // consulta R$0 — handler agora ignora; histórico no painel
    );
  }

  /**
   * Converter argumento para string de forma segura (evita referências circulares)
   */
  private safeStringify(arg: any): string {
    if (arg === null || arg === undefined) {
      return String(arg);
    }
    
    if (typeof arg === 'string') {
      return arg;
    }
    
    if (typeof arg === 'number' || typeof arg === 'boolean') {
      return String(arg);
    }
    
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`;
    }
    
    if (arg instanceof Date) {
      return arg.toISOString();
    }
    
    // Tentar stringify com detecção de referências circulares
    try {
      const seen = new WeakSet();
      return JSON.stringify(arg, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular]';
          }
          seen.add(value);
        }
        // Filtrar propriedades problemáticas comuns
        if (key === 'src' || key === 'parent' || key === 'ownerDocument' || key === 'defaultView') {
          return undefined;
        }
        return value;
      });
    } catch (error) {
      // Se ainda assim falhar, usar representação alternativa
      try {
        return String(arg);
      } catch {
        return '[Object]';
      }
    }
  }

  /**
   * Converter array de argumentos para mensagem segura
   */
  private argsToMessage(args: any[]): string {
    return args.map(arg => this.safeStringify(arg)).join(' ');
  }

  /**
   * Inicializar tracking de erros do console
   */
  initializeErrorTracking(userId: string) {
    if (this.initialized) return; // Evitar múltiplas inicializações
    
    this.initialized = true;

    // Interceptar console.error
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      originalConsoleError.apply(console, args);
      try {
        // Verificar se userId é válido antes de tentar logar
        if (userId && userId.trim() !== '') {
          const message = this.argsToMessage(args);
          if (this.isNoiseError(message)) return;
          
          // Filtrar erros esperados/informativos que não devem ser logados como erros críticos
          const isExpectedError = 
            message.includes('Missing or insufficient permissions') ||
            message.includes('permission-denied') ||
            message.includes('Erro no listener do documento do usuário') ||
            message.includes('Índice não encontrado') ||
            message.includes('Query alternativa') ||
            message.includes('índice não disponível') ||
            message.includes('The query requires an index') ||
            message.includes('requires an index') ||
            message.includes('create_composite') ||
            message.includes('Erro ao carregar execuções de automação') ||
            message.includes('Erro ao carregar dados') ||
            message.includes('[Meta pixel]') ||
            message.includes('Meta pixel') ||
            message.includes('is unavailable on this website') ||
            message.includes('traffic permission settings') ||
            message.includes('646948901744249') ||
            (message.includes('[FACEBOOK-AUTH]') && (
              message.includes('HTTPS') ||
              message.includes('não está em HTTPS') ||
              message.includes('popup-closed-by-user') ||
              message.includes('janela do Facebook foi fechada')
            )) ||
            message.includes('Facebook bloqueou o login porque a página não está em HTTPS') ||
            message.includes('Facebook requer HTTPS') ||
            message.includes('Erro esperado ao conectar Facebook') ||
            (message.includes('auth/invalid-credential-or-provider-id') && message.includes('HTTPS')) ||
            (message.includes('[AUTH]') && message.includes('HTTPS requerido'));
          
          // Não logar erros esperados/informativos
          if (isExpectedError) {
            return; // Não logar este erro
          }
          
          this.logError({
            message,
            stack: args.find(arg => arg?.stack)?.stack,
            timestamp: new Date(),
            page: window.location.pathname,
            userAgent: navigator.userAgent,
            severity: 'error'
          }, userId);
        }
      } catch (error) {
        // Ignorar erros de logging para evitar loops
        console.debug('Erro ao logar erro:', error);
      }
    };

    // Interceptar console.warn
    const originalConsoleWarn = console.warn;
    console.warn = (...args: any[]) => {
      originalConsoleWarn.apply(console, args);
      try {
        // Verificar se userId é válido e apenas logar warnings importantes
        if (userId && userId.trim() !== '') {
          const message = this.argsToMessage(args);
          if (this.isNoiseError(message)) return;
          // Não logar avisos informativos (são esperados e não são erros)
          const isInformationalWarning = 
            message.includes('Missing or insufficient permissions') ||
            message.includes('permission-denied') ||
            message.includes('Erro no listener do documento do usuário') ||
            message.includes('Índice não encontrado') ||
            message.includes('Query alternativa') ||
            message.includes('índice não disponível');
          
          // Apenas logar warnings importantes (erros reais)
          if ((message.includes('Error') || message.includes('Failed')) && !isInformationalWarning) {
            this.logError({
              message,
              stack: args.find(arg => arg?.stack)?.stack,
              timestamp: new Date(),
              page: window.location.pathname,
              userAgent: navigator.userAgent,
              severity: 'warning'
            }, userId);
          }
        }
      } catch (error) {
        // Ignorar erros de logging para evitar loops
        console.debug('Erro ao logar warning:', error);
      }
    };

    // Interceptar window.onerror
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (originalOnError) {
        originalOnError(message, source, lineno, colno, error);
      }
      // Verificar se userId é válido antes de tentar logar
      if (userId && userId.trim() !== '') {
        const msg = String(message);
        if (!this.isNoiseError(msg) && !this.isNoiseError(error?.message || '')) {
          this.logError({
            message: msg,
            stack: error?.stack,
            source: `${source}:${lineno}:${colno}`,
            timestamp: new Date(),
            page: window.location.pathname,
            userAgent: navigator.userAgent,
            severity: 'error'
          }, userId);
        }
      }
      return false;
    };

    // Interceptar unhandledrejection
    window.addEventListener('unhandledrejection', (event) => {
      // Verificar se userId é válido antes de tentar logar
      if (userId && userId.trim() !== '') {
        const msg = event.reason?.message || String(event.reason);
        if (this.isNoiseError(msg)) return;
        this.logError({
          message: msg,
          stack: event.reason?.stack,
          timestamp: new Date(),
          page: window.location.pathname,
          userAgent: navigator.userAgent,
          severity: 'error'
        }, userId);
      }
    });
  }

  /**
   * Registrar acesso a uma página
   */
  async logPageView(pageName: string, pagePath: string, userId: string): Promise<void> {
    try {
      // Calcular duração da página anterior
      if (this.currentPage && this.currentPageStartTime) {
        const duration = Math.floor((Date.now() - this.currentPageStartTime) / 1000);
        if (duration > 0) {
          await this.savePageView(this.currentPage, duration, userId);
        }
      }

      // Salvar log de acesso à página atual
      const logsRef = collection(db, 'users', userId, 'usageLogs');
      await addDoc(logsRef, {
        type: 'page_view',
        pageName,
        pagePath,
        startTime: serverTimestamp(),
        endTime: serverTimestamp(),
        durationMinutes: 0,
        moduleName: pageName,
        metadata: {
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight
        }
      });

      // Enviar page view para Google Analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        try {
          (window as any).gtag('config', 'G-K7SE125H5D', {
            page_path: pagePath,
            page_title: pageName,
          });
        } catch (error) {
          console.debug('Erro ao enviar page view para Google Analytics:', error);
        }
      }

      this.currentPage = pageName;
      this.currentPageStartTime = Date.now();
    } catch (error: any) {
      // Silenciar erro de permissão e "documento já existe" (retry/race) - não é crítico
      if (error?.code === 'permission-denied') return;
      if (error?.code === 'already-exists' || error?.message?.includes('already exists') || error?.message?.includes('Document already exists')) return;
      console.error('Erro ao registrar acesso à página:', error);
    }
  }

  /**
   * Registrar uso de uma funcionalidade
   */
  async logFeatureUsage(
    featureName: string,
    featureCategory: string,
    action: string = 'use',
    userId: string,
    details?: Record<string, any>
  ): Promise<void> {
    try {
      const logsRef = collection(db, 'users', userId, 'usageLogs');
      await addDoc(logsRef, {
        type: 'feature_usage',
        featureName,
        featureCategory,
        action,
        startTime: serverTimestamp(),
        endTime: serverTimestamp(),
        durationMinutes: 0,
        moduleName: featureCategory,
        metadata: {
          ...details,
          page: window.location.pathname,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error: any) {
      if (error?.code === 'already-exists' || error?.message?.includes('already exists') || error?.message?.includes('Document already exists')) return;
      console.error('Erro ao registrar uso de funcionalidade:', error);
    }
  }

  /**
   * Registrar erro
   */
  async logError(error: ErrorLog, userId: string): Promise<void> {
    try {
      // Verificar se userId é válido antes de tentar salvar
      if (!userId || userId.trim() === '') {
        console.debug('⚠️ [USAGE-TRACKING] userId inválido, pulando salvamento de erro');
        return;
      }

      // Limitar quantidade de erros salvos (evitar spam)
      if (this.errorLogs.length > 100) {
        this.errorLogs.shift();
      }
      this.errorLogs.push(error);

      // Não tentar salvar se o erro for de permissão (evita loop)
      if (error.message?.includes('Missing or insufficient permissions') || 
          error.message?.includes('permission-denied')) {
        console.debug('⚠️ [USAGE-TRACKING] Erro de permissão detectado, não salvando para evitar loop');
        return;
      }

      // Verificar se userId é válido e corresponde ao usuário autenticado
      try {
        const errorsRef = collection(db, 'users', userId, 'errorLogs');
        await addDoc(errorsRef, {
          ...error,
          timestamp: serverTimestamp()
        });
      } catch (err: any) {
        // Não logar erro de salvamento de erro para evitar loop
        // Apenas logar se não for erro de permissão (para não poluir o console)
        if (err?.code !== 'permission-denied' && 
            !err?.message?.includes('Missing or insufficient permissions')) {
          console.debug('Erro ao salvar log de erro:', err);
        }
      }
    } catch (error) {
      // Ignorar erros de logging para evitar loops
      console.debug('Erro ao processar log de erro:', error);
    }
  }

  private async savePageView(pageName: string, duration: number, userId: string): Promise<void> {
    try {
      const logsRef = collection(db, 'users', userId, 'usageLogs');
      await addDoc(logsRef, {
        type: 'page_view',
        pageName,
        pagePath: window.location.pathname,
        startTime: Timestamp.fromDate(new Date(this.currentPageStartTime)),
        endTime: serverTimestamp(),
        durationMinutes: duration / 60,
        moduleName: pageName,
        metadata: {
          userAgent: navigator.userAgent
        }
      });
    } catch (error: any) {
      if (error?.code === 'already-exists' || error?.message?.includes('already exists') || error?.message?.includes('Document already exists')) return;
      console.error('Erro ao salvar page view:', error);
    }
  }

  /**
   * Limpar estado ao fazer logout
   */
  reset() {
    this.currentPage = '';
    this.currentPageStartTime = Date.now();
    this.errorLogs = [];
    this.pageViews = [];
    this.featureUsage = [];
    this.initialized = false;
  }
}

export default new UsageTrackingService();

